import { useEffect } from "react";

declare const __SPARELYNK_SITE_URLS__: readonly string[];

const DEFAULT_SITE_URL = "https://sparelynk.com/";
const DEFAULT_TITLE = "SpareLynk | Trusted Auto Parts Marketplace";
const DEFAULT_DESCRIPTION =
  "Shop quality auto parts from verified sellers on SpareLynk. Compare products, check vehicle compatibility, read buyer reviews, and order securely.";
const DEFAULT_SOCIAL_IMAGE = "/sparelynk-og.png";

function normalizeSiteUrl(value: string): string | null {
  const candidate = value.trim();

  if (!candidate) {
    return null;
  }

  try {
    const url = new URL(candidate);

    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return null;
    }

    url.hash = "";
    url.search = "";
    url.pathname = "/";
    return url.toString();
  } catch {
    return null;
  }
}

function resolveConfiguredSiteUrls(): string[] {
  const configured =
    typeof __SPARELYNK_SITE_URLS__ !== "undefined"
      ? Array.from(__SPARELYNK_SITE_URLS__)
      : [];
  const normalized = configured
    .map(normalizeSiteUrl)
    .filter((value): value is string => Boolean(value));

  return Array.from(new Set(normalized.length > 0 ? normalized : [DEFAULT_SITE_URL]));
}

export const SITE_URLS = resolveConfiguredSiteUrls();
export const PRIMARY_SITE_URL = SITE_URLS[0] ?? DEFAULT_SITE_URL;

export function getAbsoluteUrl(value = "/"): string {
  const candidate = String(value ?? "").trim();

  if (!candidate) {
    return PRIMARY_SITE_URL;
  }

  try {
    return new URL(candidate, PRIMARY_SITE_URL).toString();
  } catch {
    return PRIMARY_SITE_URL;
  }
}

function normalizePathname(pathname: string): string {
  const trimmed = String(pathname ?? "").trim();

  if (!trimmed || trimmed === "/") {
    return "/";
  }

  let pathOnly = trimmed;

  try {
    if (/^https?:\/\//i.test(trimmed)) {
      pathOnly = new URL(trimmed).pathname;
    }
  } catch {
    pathOnly = "/";
  }

  const withoutQueryOrHash = pathOnly.split(/[?#]/, 1)[0] || "/";
  const withLeadingSlash = withoutQueryOrHash.startsWith("/")
    ? withoutQueryOrHash
    : `/${withoutQueryOrHash}`;

  return withLeadingSlash.length > 1
    ? withLeadingSlash.replace(/\/+$/, "")
    : withLeadingSlash;
}

export function getCanonicalUrl(pathname = "/"): string {
  return getAbsoluteUrl(normalizePathname(pathname));
}

function getMetaElements(attribute: "name" | "property", key: string) {
  return Array.from(
    document.head.querySelectorAll<HTMLMetaElement>(`meta[${attribute}]`),
  ).filter((element) => element.getAttribute(attribute) === key);
}

function setMeta(
  attribute: "name" | "property",
  key: string,
  content?: string,
) {
  const elements = getMetaElements(attribute, key);

  if (!content) {
    elements.forEach((element) => element.remove());
    return;
  }

  let element = elements[0];

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    element.dataset.sparelynkSeo = "true";
    document.head.appendChild(element);
  }

  element.content = content;
  elements.slice(1).forEach((duplicate) => duplicate.remove());
}

function setCanonicalLink(href: string) {
  const canonicalLinks = Array.from(
    document.head.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]'),
  );
  let canonical = canonicalLinks[0];

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    canonical.dataset.sparelynkSeo = "true";
    document.head.appendChild(canonical);
  }

  canonical.href = getCanonicalUrl(href);
  canonicalLinks.slice(1).forEach((duplicate) => duplicate.remove());
}

function setJsonLd(
  value: Record<string, unknown> | Array<Record<string, unknown>> | undefined,
) {
  document.head
    .querySelectorAll('script[type="application/ld+json"][data-sparelynk-json-ld]')
    .forEach((element) => element.remove());

  if (!value) {
    return;
  }

  const entries = Array.isArray(value) ? value : [value];

  entries.forEach((entry) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.sparelynkJsonLd = "true";
    script.textContent = JSON.stringify(entry).replace(/</g, "\\u003c");
    document.head.appendChild(script);
  });
}

function toRobotsContent(index: boolean, follow: boolean): string {
  return [index ? "index" : "noindex", follow ? "follow" : "nofollow"]
    .concat(["max-image-preview:large", "max-snippet:-1", "max-video-preview:-1"])
    .join(", ");
}

function resolveCanonical(canonicalUrl: string | undefined, pathname: string): string {
  if (!canonicalUrl) {
    return getCanonicalUrl(pathname);
  }

  try {
    const parsed = new URL(canonicalUrl, PRIMARY_SITE_URL);
    return getCanonicalUrl(parsed.pathname);
  } catch {
    return getCanonicalUrl(pathname);
  }
}

export type SeoProps = {
  title?: string;
  description?: string;
  pathname?: string;
  canonicalUrl?: string;
  image?: string;
  imageAlt?: string;
  type?: "website" | "product" | "article";
  keywords?: string[];
  index?: boolean;
  follow?: boolean;
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
};

export function Seo({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  pathname = "/",
  canonicalUrl,
  image = DEFAULT_SOCIAL_IMAGE,
  imageAlt,
  type = "website",
  keywords = [],
  index = true,
  follow = true,
  jsonLd,
}: SeoProps) {
  useEffect(() => {
    const canonical = resolveCanonical(canonicalUrl, pathname);
    const resolvedImage = image ? getAbsoluteUrl(image) : undefined;
    const robots = toRobotsContent(index, follow);

    document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "robots", robots);
    setMeta("name", "googlebot", robots);
    setMeta("name", "keywords", keywords.length > 0 ? keywords.join(", ") : undefined);

    setMeta("property", "og:site_name", "SpareLynk");
    setMeta("property", "og:locale", "en_US");
    setMeta("property", "og:type", type);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:image", resolvedImage);
    setMeta("property", "og:image:secure_url", resolvedImage?.startsWith("https://") ? resolvedImage : undefined);
    setMeta("property", "og:image:alt", resolvedImage ? imageAlt || title : undefined);
    setMeta(
      "property",
      "og:image:width",
      resolvedImage?.endsWith("/sparelynk-og.png") ? "1200" : undefined,
    );
    setMeta(
      "property",
      "og:image:height",
      resolvedImage?.endsWith("/sparelynk-og.png") ? "630" : undefined,
    );

    setMeta("name", "twitter:card", resolvedImage ? "summary_large_image" : "summary");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", resolvedImage);
    setMeta("name", "twitter:image:alt", resolvedImage ? imageAlt || title : undefined);
    setMeta("name", "twitter:url", canonical);

    setCanonicalLink(canonical);
    setJsonLd(jsonLd);
  }, [
    canonicalUrl,
    description,
    follow,
    image,
    imageAlt,
    index,
    jsonLd,
    keywords,
    pathname,
    title,
    type,
  ]);

  return null;
}

const PUBLIC_STATIC_PATHS = new Set([
  "/",
  "/about",
  "/contact",
  "/terms",
  "/privacy",
  "/buyer/home",
  "/buyer/products",
]);

export function isIndexablePublicPath(pathname: string): boolean {
  const normalized = normalizePathname(pathname);

  return (
    PUBLIC_STATIC_PATHS.has(normalized) ||
    /^\/buyer\/products\/[^/]+$/.test(normalized) ||
    /^\/buyer\/shops\/[^/]+$/.test(normalized)
  );
}

function getRouteDefaults(pathname: string) {
  const normalized = normalizePathname(pathname);

  if (normalized === "/") {
    return { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION };
  }
  if (normalized === "/about") {
    return {
      title: "About SpareLynk | Connected Auto Parts Marketplace",
      description:
        "Learn how SpareLynk connects auto-parts buyers, verified sellers, couriers, and marketplace operations in one platform.",
    };
  }
  if (normalized === "/contact") {
    return {
      title: "Contact SpareLynk | Marketplace Support",
      description:
        "Contact SpareLynk for help with marketplace accounts, auto parts, sellers, orders, payments, delivery, and support.",
    };
  }
  if (normalized === "/terms") {
    return {
      title: "Terms of Service | SpareLynk",
      description: "Read the terms and conditions that govern use of the SpareLynk auto-parts marketplace.",
    };
  }
  if (normalized === "/privacy") {
    return {
      title: "Privacy Policy | SpareLynk",
      description: "Learn how SpareLynk collects, uses, protects, and manages information across the marketplace.",
    };
  }
  if (normalized === "/buyer/home") {
    return {
      title: "Auto Parts Marketplace | SpareLynk",
      description:
        "Discover quality auto parts, car parts, and vehicle replacement parts from verified SpareLynk sellers with fitment information.",
    };
  }
  if (normalized === "/buyer/products") {
    return {
      title: "Shop Auto Parts Online | SpareLynk",
      description:
        "Browse automotive replacement parts from verified sellers on SpareLynk. Compare pricing, availability, ratings, and vehicle fitment.",
    };
  }
  if (/^\/buyer\/products\/[^/]+$/.test(normalized)) {
    return {
      title: "Auto Part Details | SpareLynk",
      description: DEFAULT_DESCRIPTION,
    };
  }
  if (/^\/buyer\/shops\/[^/]+$/.test(normalized)) {
    return {
      title: "Seller Shop | SpareLynk",
      description: "Browse public auto-parts listings and buyer reviews from a SpareLynk seller shop.",
    };
  }

  return {
    title: "SpareLynk",
    description: "Sign in to securely access your SpareLynk marketplace account.",
  };
}

let lastDefaultPathname: string | null = null;

export function applyRouteSeoDefaults(
  pathname: string,
  options: { resetPublicMetadata?: boolean } = {},
) {
  const normalizedPathname = normalizePathname(pathname);
  const indexable = isIndexablePublicPath(normalizedPathname);
  const routeChanged = lastDefaultPathname !== normalizedPathname;
  lastDefaultPathname = normalizedPathname;
  const robots = toRobotsContent(indexable, indexable);

  setMeta("name", "robots", robots);
  setMeta("name", "googlebot", robots);

  if (indexable && !routeChanged && !options.resetPublicMetadata) {
    return;
  }

  const { title, description } = getRouteDefaults(normalizedPathname);
  const canonical = getCanonicalUrl(normalizedPathname);
  const socialImage = getAbsoluteUrl(DEFAULT_SOCIAL_IMAGE);

  document.title = title;
  setMeta("name", "description", description);
  setMeta("name", "keywords", undefined);
  setMeta("property", "og:site_name", "SpareLynk");
  setMeta("property", "og:locale", "en_US");
  setMeta("property", "og:type", "website");
  setMeta("property", "og:title", title);
  setMeta("property", "og:description", description);
  setMeta("property", "og:url", canonical);
  setMeta("property", "og:image", socialImage);
  setMeta("property", "og:image:secure_url", socialImage.startsWith("https://") ? socialImage : undefined);
  setMeta("property", "og:image:alt", "SpareLynk trusted auto parts marketplace");
  setMeta("property", "og:image:width", "1200");
  setMeta("property", "og:image:height", "630");
  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", title);
  setMeta("name", "twitter:description", description);
  setMeta("name", "twitter:image", socialImage);
  setMeta("name", "twitter:image:alt", "SpareLynk trusted auto parts marketplace");
  setMeta("name", "twitter:url", canonical);
  setCanonicalLink(canonical);
  setJsonLd(undefined);
}
